import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilterPanel, { type FilterGroup } from '@/components/feature/FilterPanel';
import Pagination from '@/components/feature/Pagination';
import AnimeCard from '@/components/feature/AnimeCard';
import { animeList, categories, regionOptions, yearOptions, sortOptions } from '@/mocks/anime';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const PAGE_SIZE = 8;

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const currentType = type ? decodeURIComponent(type) : '少年';
  const categoryMeta = categories.find((item) => item.name === currentType);

  const [values, setValues] = useState({
    type: currentType,
    tag: '全部',
    region: '全部',
    year: '全部',
  });
  const [sort, setSort] = useState('update');
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setValues({ type: currentType, tag: '全部', region: '全部', year: '全部' });
    setSort('update');
    setVisible(PAGE_SIZE);
  }, [currentType]);

  const tagOptions = useMemo(() => {
    const tagSet = new Set<string>();
    animeList
      .filter((anime) => anime.type === currentType)
      .forEach((anime) => anime.tags.forEach((tag) => tagSet.add(tag)));
    return ['全部', ...Array.from(tagSet)];
  }, [currentType]);

  const groups: FilterGroup[] = [
    { key: 'type', label: '类型', options: categories.map((item) => item.name) },
    { key: 'tag', label: '标签', options: tagOptions },
    { key: 'region', label: '地区', options: regionOptions },
    { key: 'year', label: '年份', options: yearOptions.map(String) },
  ];

  const filtered = useMemo(() => {
    let list = animeList.filter((anime) => anime.type === values.type);
    if (values.tag !== '全部') {
      list = list.filter((anime) => anime.tags.includes(values.tag));
    }
    if (values.region !== '全部') {
      list = list.filter((anime) => anime.region === values.region);
    }
    if (values.year !== '全部') {
      list = list.filter((anime) => anime.year === Number(values.year));
    }
    const sorted = [...list];
    if (sort === 'update') {
      sorted.sort((a, b) => (a.updateDate < b.updateDate ? 1 : -1));
    } else if (sort === 'hot') {
      sorted.sort((a, b) => b.views - a.views);
    } else {
      sorted.sort((a, b) => b.rating - a.rating);
    }
    return sorted;
  }, [values, sort]);

  const shown = filtered.slice(0, visible);

  useDocumentMeta(
    `【${currentType}】动漫大全-动漫在线观看`,
    `提供丰富的${currentType}动漫资源，支持分类筛选，轻松找到心仪作品。`,
    `${currentType}动漫,${currentType}动漫大全,动漫在线观看,动漫分类,动漫筛选`,
  );

  const handleChange = (key: string, value: string) => {
    if (key === 'type') {
      navigate(`/category/${encodeURIComponent(value)}`);
      return;
    }
    setValues((prev) => ({ ...prev, [key]: value }));
    setVisible(PAGE_SIZE);
  };

  const handleReset = () => {
    setValues({ type: currentType, tag: '全部', region: '全部', year: '全部' });
    setSort('update');
    setVisible(PAGE_SIZE);
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: `${currentType}动漫` }]} />

      <div className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
          {currentType}动漫
        </h1>
        <p className="text-sm text-foreground-500 mt-2 max-w-2xl leading-relaxed">
          {categoryMeta?.description ?? `汇聚优质${currentType}动漫资源，支持多维筛选与排序，帮你快速找到心仪作品。`}
        </p>
      </div>

      <FilterPanel
        groups={groups}
        values={values}
        onChange={handleChange}
        sortOptions={sortOptions}
        sortValue={sort}
        onSortChange={(value) => {
          setSort(value);
          setVisible(PAGE_SIZE);
        }}
        onReset={handleReset}
        resultCount={filtered.length}
      />

      <div className="mt-6">
        {shown.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {shown.map((anime) => (
                <AnimeCard key={anime.id} anime={anime} />
              ))}
            </div>
            <Pagination
              total={filtered.length}
              pageSize={PAGE_SIZE}
              visible={visible}
              onChangeVisible={setVisible}
            />
          </>
        ) : (
          <div className="rounded-lg bg-background-50 border border-background-200 px-6 py-16 text-center">
            <span className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </span>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
              没有找到符合条件的动漫
            </h2>
            <p className="text-sm text-foreground-500 mb-6">
              试试更换筛选条件，或重置筛选查看全部{currentType}动漫。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
              <Link
                to="/search"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-background-100 hover:bg-background-200 text-foreground-800 font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-search-line"></i>
                去搜索
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}